import * as React from "react";
import {
  Collapse,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@material-ui/core";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@material-ui/icons";

import { formatMoney } from "lib/helpers";
import { Source } from "services/valuation/types";
import theme from "theme";

import { SourceBreakdownChartAndTableProps } from "./SourceBreakdown";

const useRowStyles = makeStyles({
  root: {
    "& > *": {
      borderBottom: "none",
      padding: "0 5px",
      height: "30px",
    },
  },
});

type SourceBreakdownTableRowProps = {
  color: string;
  source: Source;
  totalValue: number;
};

export function Row(props: SourceBreakdownTableRowProps) {
  const { color, source, totalValue } = props;
  const [open, setOpen] = React.useState(true);
  const classes = useRowStyles();

  return (
    <>
      <TableRow className={classes.root}>
        <TableCell
          style={{
            width: "15px",
            color: color,
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          {Math.round((source.earnings / totalValue) * 100)}%
        </TableCell>
        <TableCell
          style={{
            width: "30px",
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          ${formatMoney(source.earnings, 0)}
        </TableCell>
        <TableCell>
          {source.name}
          {!!source.earning_details?.length && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      {!!source.earning_details?.length && open && (
        <TableRow className={classes.root}>
          <TableCell colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Table
                style={{ marginBottom: "10px" }}
                aria-label="earning-details-table"
              >
                <TableBody>
                  {source.earning_details.map(
                    (earningDetails: Source, index) => (
                      <TableRow
                        key={`row-${earningDetails.name}-${index}`}
                        className={classes.root}
                      >
                        <TableCell />
                        <TableCell
                          style={{
                            width: "15px",
                            color: color,
                            fontWeight: theme.typography.fontWeightMedium,
                          }}
                        >
                          {Math.round(
                            (earningDetails.earnings / source.earnings) * 100,
                          )}
                          %
                        </TableCell>
                        <TableCell
                          style={{
                            width: "30px",
                            fontWeight: theme.typography.fontWeightMedium,
                          }}
                        >
                          ${formatMoney(earningDetails.earnings, 0)}
                        </TableCell>
                        <TableCell>{earningDetails.name}</TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function SourceBreakdownTable(props: SourceBreakdownChartAndTableProps) {
  const { data, colors, totalValue } = props;

  return (
    <div style={{ flexGrow: 1, minWidth: "310px" }}>
      <Table aria-label="source-breakdown-table">
        <TableBody>
          {data.map((source: Source, index) => (
            <Row
              key={`row-${source.name}-${index}`}
              source={source}
              color={colors[index % colors.length]}
              totalValue={totalValue}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
